/*
 * Copyright (c) 2026.
 * # Autor: TECINCO TECNOLOGIA
 * # Dev: Alejandro Meriño Rodrìguez
 * # Versión: 2.0.0
 */

import {inject, Injectable} from "@angular/core";
import {firstValueFrom} from "rxjs";
import {RelatedController} from "@app/controllers/related.controller";
import {Part} from "@app/interface/part";
import {RoService} from "@serv/ro.service";
import {PlayMessageService} from "@llm/service/play_message.service";
import {ToolsAiDialogsService} from "@llm/service/tools_ai-dialogs.service";
import {Tools_aiConsoleService} from "@llm/service/tools_ai-console.service";
import {LayoutService} from "@app/service/layout.service";
import {RoListStateGroup} from "@app/shared/pipes/ro-state-group.pipe";
import {Utilities} from "@app/class/utilities";
import {MkgOS} from "@app/class/mkg-os";
import {ToolResponse} from '@llm/models/schemas/agents/tools_utils';
import {
    AddPartsToOsBudgetRequest, F_Part_By_Code, F_Part_By_Description,
    PartItemRequest, PartOsBudgetRequest,
    PartSelectionDialogData,
    ProcessedPartResult
} from "@llm/models/schemas/agents/agree_parts_to_os_budget/interfaces";
import {ApiPaginatedOSQueryParams} from "@serv/pagination.service";
import {UtilsMiscellaneousService} from '@llm/models/utils_micellanius.service';
import {Client} from "@interface/client";
import {HelpersAgent} from "@llm/models/schemas/agents/agree_parts_to_os_budget/helpers";

/**
 * Tool para adição de peças a Ordem de Serviço ou Orçamento
 *
 * Responsabilidades:
 * - Validar existência e estado da OS
 * - Buscar peças por código (prioritário) ou descrição
 * - Lidar com múltiplos resultados (diálogo de seleção)
 * - Validar NCM para empresas fiscais
 * - Verificar peças repetidas (oferecer opções: somar, separar, cancelar)
 * - Validar quantidade vs estoque disponível
 * - Adicionar peças via RelatedController
 * - Processar itens relacionados automaticamente
 * - Propagar mecânico automaticamente
 * - Registrar logs de auditoria
 */
@Injectable({providedIn: 'root'})
export class Tool_add_parts_os_budget {

    // Dependencies
    private _relatedController = inject(RelatedController);
    private _roService = inject(RoService);
    private _toolsAiDialogsService = inject(ToolsAiDialogsService);
    private _playMessageService = inject(PlayMessageService);
    private _console = inject(Tools_aiConsoleService);
    private _layout = inject(LayoutService);
    private _utilities = inject(UtilsMiscellaneousService);
    private _helperAgent = inject(HelpersAgent)

    // Timeout para operações longas (60*2 segundos)
    private readonly OPERATION_TIMEOUT = 120000;
    private isOS: string='';

    /**
     * Método principal da tool
     * Processa requisição de adição de peças à OS/Orçamento
     *
     * @returns ToolResponse com resultado do processamento
     * @param _request
     */
    async processAgentData(_request: AddPartsToOsBudgetRequest): Promise<ToolResponse> {
        const { os_budget, parts, dry_run} = _request;

        const timeoutPromise = new Promise<ToolResponse>((_, reject) => {
            setTimeout(() => reject(new Error('TIMEOUT: Operation time out, more than 120 seconds')), this.OPERATION_TIMEOUT);
        });

        const operationPromise = (async () => {
            try {
                if (!parts || parts.length === 0) {
                    return this._helperAgent.errorResponse(
                        'No parts were provided. It is necessary to provide at least one part to add to the Service Order.',
                        'processAgentData()'
                    );
                }

                const roResult = await this._validateAndGetOS(os_budget);

                if (Array.isArray(roResult)) {
                    const combinedReason = roResult
                        .map(res => res.reason)
                        .filter(reason => !!reason)
                        .join(' | ');

                    return this._helperAgent.errorResponse(combinedReason, 'processAgentData()');
                }

                const ro = roResult;



                if (!ro) {
                    return this._helperAgent.errorResponse(
                        `${os_budget.isBudget ? 'Budget': 'OS'} no found with search criteria provided. Verify and try again.`,
                        'processAgentData()'
                    );
                }

                this.isOS = ro.id;

                const stateValidation: ToolResponse = this._helperAgent.validateOSState(ro);
                if (stateValidation.status !='ok') {
                    return stateValidation;
                }

                // 3. Processar cada peça
                const results: ProcessedPartResult[] = [];
                for (const partRequest of parts) {
                    const result = await this._processSinglePart(ro, partRequest, dry_run);
                    results.push(result);
                }

                // 4. Consolidar resultados
                return this._helperAgent.consolidateResponse(results, ro);

            } catch (error: any) {
                this._console.error("[Tool_add_parts_os_budget] Erro crítico:", error);

                // Verifica se é erro de OS em uso
                if (error?.message?.startsWith('OS_EM_USO|')) {
                    const parts = error.message.split('|');
                    const osId = parts[1];
                    const minutesAgo = parts[2];
                    return this._helperAgent.errorResponse(
                        `The OS ${osId} is currently being edited by another user (${minutesAgo} minutes ago). ` +
                        `Please wait for them to finish editing or ask them to close the OS before trying again.`
                    );
                }

                return this._helperAgent.errorResponse(`Erro interno ao processar peças: ${error.message}`);
            }
        })();

        try {
            return await Promise.race([operationPromise, timeoutPromise]);
        } catch (error: any) {
            if (error.message?.startsWith('TIMEOUT:')) {
                return this._helperAgent.errorResponse(
                    `Time limit exceeded. The operation is taking longer than expected. ` +
                    `Check if the OS (Order Service) is being edited by another user and try again.`
                );
            }
            throw error;
        } finally {
            debugger;
            if(this.isOS){
                await this._helperAgent.closeSession(this.isOS);
            }
            //this._layout.removeLoaderTrigger("Tool_add_parts_os_budget.processAgentData()");
        }
    }

    /**
     * Valida y obtiene una Orden de Servicio (OS) o Presupuesto buscando por diferentes criterios
     *
     * Este método intenta obtener una OS/Presupuesto utilizando una jerarquía de búsqueda:
     * 1. ID (prioridad máxima)
     * 2. Código del sistema
     * 3. Placa del vehículo
     * 4. Nombre del cliente
     *
     * @param osRequest - Solicitud que puede ser un string JSON o un objeto PartOsBudgetRequest
     *                    conteniendo los criterios de búsqueda (id, code, plate, name_customer, isBudget)
     * @returns Promesa que se resuelve en:
     *          - MkgOS: Cuando se encuentra exitosamente la OS/Presupuesto
     *          - ToolResponse[]: Array de respuestas de error cuando falla la búsqueda
     *
     * @remarks
     * - Si se proporciona el ID, se intenta la búsqueda directa ignorando otros criterios
     * - Para búsquedas por código, placa o nombre, si hay múltiples resultados se solicita al usuario que seleccione
     * - El parámetro isBudget determina si se busca un presupuesto o una OS abierta
     * - Maneja errores de concurrencia cuando otro usuario está editando la OS
     */
    private async _validateAndGetOS(osRequest: PartOsBudgetRequest): Promise<MkgOS | ToolResponse[]> {

        /**
        * Prioridade: ID > Code > Plate > Name
        */
        let data = osRequest;
        if (typeof osRequest === 'string') {
            try {
                data = JSON.parse(osRequest);
            } catch (e) {
                console.error('[Tool_add_parts_os_budget] Error al parsear os_budget:', e);
                return [this._helperAgent.errorResponse(
                    'Invalid JSON provided for os_budget. Please provide a valid JSON string.',
                    '_validateAndGetOS()'
                )];
            }
        }

        const { isBudget, id, code, name_customer, plate } = data;

        try {
            let controlToolResponse: ToolResponse[] = [];
            let descritiveText: string = isBudget ? 'Budget' : 'OS';
            const states = isBudget ? ['opened', 'budget'] : ['opened'];

            /**
             * Búsqueda por ID - Prioridad máxima
             * Intento directo de obtención por identificador único
             */
            if (id && this._helperAgent.isValidObjectId(id)) {
                try {
                    return await this._roService.get(id);
                } catch (e) {
                    controlToolResponse.push( this._helperAgent.errorResponse(
                       `Error searching for the ${descritiveText} by its ID, likely because it does not exist in the database: ID => ${id}`,
                        '_validateAndGetOS()'
                    ));
                }
            }

            /**
             * Búsqueda por código del sistema
             * Realiza búsqueda paginada y maneja múltiples resultados solicitando selección al usuario
             */
            if (code) {
                try {
                    const codeNum = Number(code);
                    const searchParams: ApiPaginatedOSQueryParams = isBudget
                        ? { budgetCode: codeNum, all: 0, limit: 1 }
                        : { codeSystem: codeNum, all: 0, limit: 1 };

                    const _result = await firstValueFrom(
                        this._roService.getPaginated(states[0] as RoListStateGroup, searchParams)
                    );

                    /**
                     * Manejo de resultados de búsqueda por código
                     * - 0 resultados: Error de no encontrado
                     * - 1 resultado: Retorno directo
                     * - Múltiples resultados: Solicita selección al usuario
                     */
                    if (_result.docs.length === 0) {
                        controlToolResponse.push( this._helperAgent.errorResponse(
                            `No ${descritiveText} found with the provided system code: CODE => ${code}`,
                            '_validateAndGetOS()'
                        ));
                    } else if (_result.docs.length === 1) return await this._roService.get(_result.docs[0].id);

                    else if (_result.docs.length > 1) {
                        let os_budgets: MkgOS[] = await Promise.all(_result.docs.map(doc => this._roService.get(doc.id)))
                        let os_budget: MkgOS | undefined = await this._utilities.selectFoundOsBudget(os_budgets);
                        if (os_budget) {
                            return os_budget;
                        } else {
                            controlToolResponse.push( this._helperAgent.errorResponse(
                                `The user did not select any ${descritiveText} from the results found or there was an application error`,
                                '_validateAndGetOS()'
                            ));
                        }
                    }

                } catch (e) {
                    controlToolResponse.push( this._helperAgent.errorResponse(
                        `The OS is currently being edited by another user: ${code}`,
                        '_validateAndGetOS()'
                    ));
                }
            }

            /**
             * Búsqueda por placa del vehículo
             * Obtiene todas las OS/Presupuestos asociados a la placa y maneja múltiples resultados
             */
            if (plate) {
                const _result: MkgOS[] = await this._roService.getOsByPlate(plate, {
                    isBudget: isBudget,
                    states: states
                })

                /**
                 * Procesamiento de resultados por placa
                 * - Sin resultados: Error de no encontrado
                 * - Un resultado: Retorno directo
                 * - Múltiples resultados: Solicita selección al usuario
                 */
                if (_result.length === 0) {
                    controlToolResponse.push( this._helperAgent.errorResponse(
                        `No ${descritiveText} found for the provided plate: ${plate}`,
                        '_validateAndGetOS()'
                    ));
                } else if (_result.length === 1) return _result[0];

                else if (_result.length > 1) {
                    let os_budget: MkgOS | undefined = await this._utilities.selectFoundOsBudget(_result);
                    if (os_budget) {
                        return os_budget;
                    } else {
                        controlToolResponse.push( this._helperAgent.errorResponse(
                            `The user did not select any ${descritiveText} from the results found for this criteria ${plate} or there was an application error`,
                            '_validateAndGetOS()'
                        ));
                    }
                }
            }

            /**
             * Búsqueda por nombre del cliente
             * Primero busca/selecciona el cliente, luego obtiene sus OS/Presupuestos asociados
             */
            if (name_customer) {
                let client: Client | undefined = await this._utilities.search_and_select_client(name_customer);
                if (!client) {
                    controlToolResponse.push( this._helperAgent.errorResponse(
                        `No found any client for the provided name: ${name_customer}`,
                        '_validateAndGetOS()'
                    ));
                }else {
                    const _result: MkgOS[] = await this._roService.getOsByClient(client.id, {states: states});

                    /**
                     * Procesamiento de resultados por cliente
                     * - Sin resultados: Error de no encontrado
                     * - Un resultado: Retorno directo
                     * - Múltiples resultados: Solicita selección al usuario
                     */
                    if (_result.length === 0) {
                        controlToolResponse.push( this._helperAgent.errorResponse(
                            `No ${descritiveText} found for the provided client name: ${name_customer}`,
                            '_validateAndGetOS()'
                        ));
                    }

                    else if (_result.length === 1) return _result[0];


                    else if(_result.length > 1) {
                        let os_budget: MkgOS | undefined = await this._utilities.selectFoundOsBudget(_result);
                        if (os_budget) {
                            return os_budget;
                        } else {
                            controlToolResponse.push( this._helperAgent.errorResponse(
                                `The user did not select any ${descritiveText} from the results found for this criteria ${name_customer} or there was an application error`,
                                '_validateAndGetOS()'
                            ));
                        }
                    }

                }
            }


            /**
             * Validación final: Si ningún criterio de búsqueda fue proporcionado o todos fallaron
             */
            if(controlToolResponse.length==0){
                controlToolResponse.push( this._helperAgent.errorResponse(
                    'No valid search criteria provided for the OS/Budget',
                    '_validateAndGetOS()'
                ));
            }

            return controlToolResponse;
        } catch (error: any) {
            /**
             * Manejo global de errores
             * Incluye detección específica de errores de sesión/concurrencia
             */
            const controlToolResponse: ToolResponse[] = [
                this._helperAgent.errorResponse(
                    'Error while searching for OS/Budget',
                    '_validateAndGetOS()'
                )
            ];

            const sessionError = error?.error?.userId || error?.error?.sessionHash;
            if (error?.status === 400 && sessionError) { // OS está sendo editada por outro usuário
                const sessionTimeStarted = new Date(+sessionError);
                const timeDiff = Date.now() - sessionTimeStarted.getTime();
                const minutesAgo = Math.floor(timeDiff / 60000);

                controlToolResponse.push( this._helperAgent.errorResponse(
                    `The OS is currently being edited by another user to ${minutesAgo} ago. Please wait for them to finish or close it`,
                    '_validateAndGetOS()'
                ));
            }

            return controlToolResponse;
        }
    }

    /**
     * Processa peça individual
     * @param ro OS de destino
     * @param request Dados da peça
     * @param dryRun Se true, apenas simula a adição
     * @returns Resultado do processamento
     */
    private async _processSinglePart(ro: MkgOS, request: PartItemRequest, dryRun?: boolean): Promise<ProcessedPartResult> {
        let part: Part | null = null;

        if (!request.code && !request.search_by_description) {
            return {
                success: false,
                error: 'Code or description not provided.'
            };
        }

        if (request.quantity !== undefined && request.quantity !== null) {
            if (request.quantity < 1) {
                return {
                    success: false,
                    partCode: request.code || request.search_by_description,
                    error: `The provided quantity (${request.quantity}) is invalid. ` +
                        `The quantity must be at least 1. ` +
                        `If not specified, the default quantity will be 1.`
                };
            }
            if (request.quantity > 1000) {
                return {
                    success: false,
                    partCode: request.code || request.search_by_description,
                    error: `The provided quantity (${request.quantity}) is too high. ` +
                        `The maximum limit is 1000 units at a time. ` +
                        `For larger quantities, please split them into multiple requests.`
                };
            }
        }

        try {
            if (request.code) {
                const searchResult: F_Part_By_Code = await this._helperAgent._findPartByCode(request.code);

                if (!searchResult.found) {
                    if (searchResult.reason === 'inactive') {
                        return {
                            success: false,
                            partCode: request.code,
                            error: `The part with code "${request.code}" exists in the catalog but is INACTIVE. ` +
                                `Inactive parts cannot be added to the Service Order. ` +
                                `Please request an administrator to reactivate the part in the system.`
                        };
                    }
                    return {
                        success: false,
                        partCode: request.code,
                        error: `Part with code "${request.code}" was not found in the catalog. ` +
                            `Please check if the code is correct and try again.`
                    };
                }

                part = searchResult.part;
            }

            else if (request.search_by_description) {
                const searchResult: F_Part_By_Description = await this._helperAgent.findPartByDescription(request.search_by_description, ro);

                if (!searchResult.found) {
                    if (searchResult.reason === 'cancelled') {
                        return {
                            success: false,
                            partCode: request.search_by_description,
                            error: `Search for "${request.search_by_description}" returned multiple parts, but the selection was cancelled. ` +
                                `Please refine the description (be more specific) or provide the part code.`
                        };
                    }
                    return {
                        success: false,
                        partCode: request.search_by_description,
                        error: `No parts were found matching the description "${request.search_by_description}". ` +
                            `Try a more specific description or use the part code.`
                    };
                }

                part = searchResult.part;
            }

             if (!part) {
                 return {
                     success: false,
                     partCode: request.code || request.search_by_description,
                     error: 'Part not found in catalog. Please verify the provided information and try again.'
                 };
             }

            // DRY RUN: Apenas simula
            if (dryRun) {
                return {
                    success: true,
                    partCode: part?.code,
                    partDescription: part?.description,
                    partId: part?.id,
                    quantity: request.quantity || 1,
                    dry_run: true,
                    preview: {
                        would_add_quantity: request.quantity || 1,
                        would_be_status: this._helperAgent.getPartStatus(ro),
                        would_have_related: part?.itemRelated?.length || 0,
                        estimated_total_value: (part?.saleValue || 0) * (request.quantity || 1)
                    }
                };
            }

            // Verifica peça repetida na OS e oferece opções
            const repeatedPart = this._helperAgent.checkRepeatedPart(ro, part);
            if (repeatedPart) {
                const decision = await this._handleRepeatedPart(ro, part, request.quantity || 1);

                if (decision.action === 'cancelled') {
                    return {
                        success: false,
                        partCode: part?.code,
                        partDescription: part?.description,
                        partId: part?.id,
                        action: 'cancelled',
                        error: `User cancelled the addition of part "${part?.code}" because it already exists in the Service Order.`
                    };
                }

                if (decision.action === 'summed_to_existing') {
                    repeatedPart.oldAmount = repeatedPart.amount();
                    repeatedPart.amount.set(repeatedPart.amount() + decision.quantity);

                    await this._relatedController.onUpdatedPart(
                        ro,
                        repeatedPart,
                        "Tool_add_parts_os_budget.sumRepeated",
                        "amount"
                    );

                    this._console.log('[Tool_add_parts_os_budget] Quantidade somada à peça existente:', part?.code);

                    return {
                        success: true,
                        partCode: part?.code,
                        partDescription: part?.description,
                        partId: part?.id,
                        quantity: decision.quantity,
                        action: 'summed_to_existing',
                        existingPartQuantity: repeatedPart.amount()
                    };
                }
            }

            // Valida quantidade vs estoque disponível
            const stockValidation = await this._helperAgent.validateStock(part, request.quantity || 1);
            if (!stockValidation.valid) {
                if (stockValidation.action === 'cancelled') {
                    return {
                        success: false,
                        partCode: part?.code,
                        error: `Quantidade (${request.quantity}) excede estoque disponível (${part.stock}). Adição cancelada pelo usuário.`
                    };
                }
                // Se action === 'use_max', ajusta quantidade
                request.quantity = part.stock;
            }

            // Valida NCM (se OS aberta e emite documento fiscal)
            const ncmValidation = this._validateNCM(part, ro);
            if (!ncmValidation.valid) {
                return {
                    success: false,
                    partCode: part?.code,
                    partDescription: part?.description,
                    partId: part?.id,
                    requiresNcmCorrection: true,
                    error: ncmValidation.reason
                };
            }

            // Determina status da peça baseado no estado da OS
            const partStatus = this._helperAgent.getPartStatus(ro);

            // Propaga mecânico automaticamente (última peça com mecânico)
            const partsWithMechanic = ro.parts.filter(p => p.mechanic && p.mechanic.trim() !== "");
            const lastPartWithMechanic = partsWithMechanic.length > 0 ? partsWithMechanic[partsWithMechanic.length - 1] : null;

            // Adiciona via RelatedController (processa itens relacionados automaticamente)
            const relatedCountBefore = part?.itemRelated?.length || 0;

            await this._relatedController.onPartAddedToRO({
                ro,
                part,
                amount: request.quantity || 1,
                additional: ro.isBudget,
                movementCaller: "Tool_add_parts_os_budget",
                mechanic: lastPartWithMechanic?.mechanic,  // Propaga mecânico
                status: partStatus
            });

            // Auditoria
            await this._helperAgent.createAuditLog(ro, part, 'add');

            this._console.log('[Tool_add_parts_os_budget] Peça adicionada com sucesso:', part?.code);

            return {
                success: true,
                partCode: part?.code,
                partDescription: part?.description,
                partId: part?.id,
                quantity: request.quantity || 1,
                relatedItemsCount: relatedCountBefore
            };

        } catch (error: any) {
            this._console.error('[Tool_add_parts_os_budget] Erro ao processar peça:', error);
            return {
                success: false,
                partCode: request.code || request.search_by_description,
                error: `Erro ao adicionar peça: ${error.message}`
            };
        }
    }

    /**
     * Lida com peça repetida, oferecendo opções ao usuário
     * @param ro OS
     * @param part Peça a verificar
     * @param requestedQuantity Quantidade solicitada
     * @returns Decisão do usuário
     */
    private async _handleRepeatedPart(ro: MkgOS, part: Part, requestedQuantity: number): Promise<{
        action: 'summed_to_existing' | 'added_separate' | 'cancelled';
        quantity: number;
    }> {
        const existingPart = ro.parts.find(p => p.part.id === part.id);

        if (!existingPart) {
            return {action: 'added_separate', quantity: requestedQuantity};
        }

        // Abrir diálogo de seleção
        const dialogData: PartSelectionDialogData = {
            items: [
                {
                    id: 'sum',
                    name: `Somar ${requestedQuantity} à quantidade existente (${existingPart.amount()})`,
                    descriptor: `Total: ${existingPart.amount() + requestedQuantity} unidades`
                },
                {
                    id: 'separate',
                    name: 'Adicionar como item separado',
                    descriptor: `Criar novo item com ${requestedQuantity} unidades`
                },
                {
                    id: 'cancel',
                    name: 'Cancelar adição',
                    descriptor: 'Não adicionar esta peça'
                }
            ],
            icon: 'warning',
            nameClass: 'ação',
            references: {
                positive: 'Selecionar',
                negative: 'Fechar'
            }
        };

        await this._playMessageService.playMessage(
            `A peça "${part.code} - ${part.description}" já existe na OS com quantidade ${existingPart.amount()}. ` +
            `Por favor, selecione uma opção no diálogo que foi aberto.`,
            true
        );

        const selectedId = await firstValueFrom(
            this._toolsAiDialogsService.openDataSelectionDialog(dialogData)
        );

        this._console.log('[Tool_add_parts_os_budget] Seleção para peça repetida:', selectedId);

        if (selectedId === 'defaultItem' || !selectedId) {
            return {action: 'cancelled', quantity: 0};
        }

        switch (selectedId) {
            case 'sum':
                return {action: 'summed_to_existing', quantity: requestedQuantity};
            case 'separate':
                return {action: 'added_separate', quantity: requestedQuantity};
            case 'cancel':
            default:
                return {action: 'cancelled', quantity: 0};
        }
    }


    /**
     * Valida NCM da peça para empresas que emitem documento fiscal
     * @param part Peça
     * @param ro OS
     * @returns Objeto com validação e mensagem de erro (se aplicável)
     */
    private _validateNCM(part: Part, ro: MkgOS): { valid: boolean; reason?: string } {
        // Só valida se OS está aberta e emite documento fiscal
        if (!(ro.isOpened && ro.operation?.docFiscal)) {
            return {valid: true};
        }

        // Validações do NCM
        if (!part.NCM) {
            return {
                valid: false,
                reason: `Peça "${part.code}" não possui NCM cadastrado. Para OS que emitem documento fiscal, o NCM é obrigatório. Corrija no catálogo.`
            };
        }

        const ncmStr = part.NCM.toString();

        if (ncmStr.length !== 8) {
            return {
                valid: false,
                reason: `Peça "${part.code}" possui NCM com ${ncmStr.length} dígitos. O NCM deve ter exatamente 8 dígitos.`
            };
        }

        if (Utilities.stringHaveOnlyEqualCharacters(ncmStr)) {
            return {
                valid: false,
                reason: `Peça "${part.code}" possui NCM inválido (${ncmStr}). NCM não pode ter todos os dígitos iguais.`
            };
        }

        return {valid: true};
    }
}
